import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUser, useClerk } from "@clerk/clerk-react";
import { toastError, presentMutationError } from "../../../../utils/showApiErrorToast";
import {
  Laptop,
  Loader2,
  Monitor,
  Smartphone,
  Tablet,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";

import {
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { LogoutIcon, type LogoutIconHandle } from "@/components/ui/logout-icon";
import { cn } from "@/lib/utils";
import { resetDashboardDateWindowForAuthSession } from "@/utils/dashboardDateWindowStorage";
import { accountSettingsNestedCardClassName } from "../accountSettingsCardShell";
import SettingCard, {
  accountSettingsListRowAsideClassName,
  accountSettingsListRowClassName,
  accountSettingsListRowFieldClassName,
  accountSettingsListRowMainClassName,
} from "./SettingCard";

interface DevicesProps {
  title: string;
}

interface DeviceInfo {
  id: string;
  deviceName: string;
  deviceType: string;
  lastActive: Date;
  isCurrent: boolean;
}

function detectDeviceType(userAgent: string): { type: string; icon: LucideIcon; name: string } {
  const ua = userAgent.toLowerCase();

  if (ua.includes("mobile") || ua.includes("android") || ua.includes("iphone")) {
    return { type: "mobile", icon: Smartphone, name: "Mobile Device" };
  }
  if (ua.includes("tablet") || ua.includes("ipad")) {
    return { type: "tablet", icon: Tablet, name: "Tablet" };
  }
  if (ua.includes("laptop") || ua.includes("macbook")) {
    return { type: "laptop", icon: Laptop, name: "Laptop" };
  }
  return { type: "desktop", icon: Monitor, name: "Desktop" };
}

function deviceIconForType(type: string): LucideIcon {
  switch (type) {
    case "mobile":
      return Smartphone;
    case "tablet":
      return Tablet;
    case "laptop":
      return Laptop;
    default:
      return Monitor;
  }
}

function getDeviceName(userAgent: string): string {
  const ua = userAgent;

  if (ua.includes("iPhone")) {
    const match = ua.match(/iPhone\s+([^;)]+)/);
    return match ? `iPhone ${match[1]}` : "iPhone";
  }
  if (ua.includes("iPad")) {
    const match = ua.match(/iPad[^;)]*/);
    return match ? match[0] : "iPad";
  }
  if (ua.includes("Macintosh")) {
    return "Mac";
  }
  if (ua.includes("Windows")) {
    const match = ua.match(/Windows\s+([^;)]+)/);
    return match ? `Windows ${match[1]}` : "Windows PC";
  }
  if (ua.includes("Linux")) {
    return "Linux PC";
  }
  if (ua.includes("Android")) {
    const match = ua.match(/Android\s+([^;)]+)/);
    return match ? `Android ${match[1]}` : "Android Device";
  }
  if (ua.includes("Chrome")) return "Chrome Browser";
  if (ua.includes("Firefox")) return "Firefox Browser";
  if (ua.includes("Safari")) return "Safari Browser";
  if (ua.includes("Edge")) return "Edge Browser";

  return "Unknown Device";
}

function formatLastActive(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

function DeviceDisconnectButton({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) {
  const logoutIconRef = useRef<LogoutIconHandle | null>(null);

  const startLogoutAnimation = useCallback(() => {
    if (disabled) return;
    void logoutIconRef.current?.startAnimation();
  }, [disabled]);

  const stopLogoutAnimation = useCallback(() => {
    void logoutIconRef.current?.stopAnimation();
  }, []);

  const hoverHandlers = useMemo(
    () =>
      disabled
        ? {}
        : {
            onPointerDownCapture: startLogoutAnimation,
            onMouseEnter: startLogoutAnimation,
            onMouseLeave: stopLogoutAnimation,
          },
    [disabled, startLogoutAnimation, stopLogoutAnimation]
  );

  return (
    <Button
      type="button"
      variant="outline-gray"
      size="sm"
      data-font="primary"
      className="shrink-0"
      disabled={disabled}
      aria-busy={disabled || undefined}
      onClick={onClick}
      {...hoverHandlers}
    >
      {disabled ? (
        <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
      ) : (
        <LogoutIcon
          ref={logoutIconRef}
          data-icon="inline-start"
          size={16}
          className="shrink-0 text-current"
          aria-hidden
        />
      )}
      Disconnect
    </Button>
  );
}

const Devices: React.FC<DevicesProps> = ({ title }) => {
  const { user } = useUser();
  const clerk = useClerk();
  const [devices, setDevices] = useState<DeviceInfo[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDevices = async () => {
      if (!user) return;

      try {
        const currentSession = clerk.session;
        const currentUserAgent = navigator.userAgent;
        const currentDeviceInfo = detectDeviceType(currentUserAgent);
        const currentDeviceName = getDeviceName(currentUserAgent);

        const deviceList: DeviceInfo[] = [];

        if (currentSession) {
          deviceList.push({
            id: currentSession.id,
            deviceName: currentDeviceName,
            deviceType: currentDeviceInfo.type,
            lastActive: new Date(),
            isCurrent: true,
          });
        }

        setDevices(deviceList);
      } catch (error) {
        console.error("Error fetching devices:", error);
      }
    };

    void fetchDevices();
  }, [user, clerk]);

  const handleDisconnect = async (deviceId: string, isCurrent: boolean) => {
    if (isCurrent) {
      try {
        resetDashboardDateWindowForAuthSession();
        await clerk.signOut();
        toast.success("Signed out", { description: "You have been signed out from this device." });
      } catch (error: unknown) {
        presentMutationError(error, "Error");
      }
      return;
    }

    setLoading(true);
    try {
      toast.success("Device disconnected", {
        description: "The device has been disconnected successfully.",
      });
    } catch (error: unknown) {
      presentMutationError(error, "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SettingCard
      title={title}
      description="Manage devices that have access to your account. You can disconnect devices you no longer use."
    >
      <div className="flex w-full min-w-0 flex-col gap-6" data-devices-list="">
        {devices.length === 0 ? (
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>No devices found</FieldDescription>
        ) : (
          devices.map((device) => {
            const DeviceIcon = deviceIconForType(device.deviceType);
            const isDisconnecting = loading;

            return (
              <div
                key={device.id}
                data-device-row=""
                className={cn(
                  accountSettingsNestedCardClassName,
                  device.isCurrent &&
                    "bg-[color-mix(in_oklab,var(--brand-tropical-surf)_5%,var(--card))]"
                )}
              >
                <div
                  data-account-settings-list-row=""
                  className={accountSettingsListRowClassName}
                >
                  <div
                    data-account-settings-list-row-main=""
                    className={accountSettingsListRowMainClassName}
                  >
                    <DeviceIcon
                      className="size-5 shrink-0 text-[color:var(--brand-tropical-surf)]"
                      aria-hidden
                    />
                    <Field className={accountSettingsListRowFieldClassName}>
                      <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>{device.deviceName}</FieldLabel>
                      <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
                        Last active: {formatLastActive(device.lastActive)}
                      </FieldDescription>
                    </Field>
                  </div>
                  <div
                    data-account-settings-list-row-aside=""
                    className={accountSettingsListRowAsideClassName}
                  >
                    <DeviceDisconnectButton
                      disabled={isDisconnecting}
                      onClick={() => void handleDisconnect(device.id, device.isCurrent)}
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </SettingCard>
  );
};

export default Devices;
